import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { organizationCleared, signOut } from '@/redux/session-slice';
import { Initials } from '@/components/ui';
import { Logo } from '@/components/Brand';

/**
 * The app shell: sidebar navigation, the organization switcher, and the signed-in
 * person.
 *
 * Navigation items that have no screen yet are rendered as disabled with a
 * plain reason, rather than hidden or linking to an empty page. Someone
 * evaluating this deployment should be able to see the shape of the whole
 * product and what is not built yet, without guessing.
 *
 * NARROW SCREENS GET A DRAWER, not a stacked sidebar. Below `lg` the sidebar
 * used to sit in the flow above the content, which pushed the actual page —
 * heading, stats, everything — off the bottom of the screen: you landed on
 * "Overview" and saw only navigation. A facilities manager checking a room on
 * their phone should see the room.
 *
 * The desktop sidebar and the mobile drawer are deliberately SEPARATE elements
 * rather than one element restyled. A single node would have to be hidden from
 * assistive technology when the drawer is closed and exposed again at desktop
 * width, from the same markup, and getting that wrong silently hides the whole
 * navigation from a screen reader. Here each one is `display: none` at the
 * width it does not belong to, which removes it from the accessibility tree
 * outright, and the drawer additionally goes `inert` while closed.
 */

interface NavItem {
  to: string;
  label: string;
  ready: boolean;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Overview', ready: true },
  { to: '/connections', label: 'Connections', ready: true },
  { to: '/rooms', label: 'Discovered rooms', ready: true },
  { to: '/mappings', label: 'Space mapping', ready: true },
  { to: '/sync-health', label: 'Sync health', ready: true },
  // The connected calendar's own people, read-only. Last, because it is
  // context rather than a step: nothing in setting the adapter up passes
  // through it. NOT ZenSpace's users — see `Directory.tsx`.
  { to: '/directory', label: 'Directory', ready: true },
  // DEV ONLY. `__DIAGNOSTICS_AVAILABLE__` is a compile-time constant, so in a
  // production build this entry is `false &&` and the whole row disappears with
  // the route — nobody is offered a link to a screen that is not there.
  ...(__DIAGNOSTICS_AVAILABLE__
    ? [{ to: '/diagnostics', label: 'Diagnostics', ready: true }]
    : []),
];

/** Matches the `lg:` breakpoint the layout switches on. */
const DESKTOP_QUERY = '(min-width: 64rem)';

export function Shell() {
  const dispatch = useAppDispatch();
  const { user, organizations, organizationId } = useAppSelector((s) => s.session);
  const { pathname } = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const drawer = useRef<HTMLDivElement>(null);

  const organization = useMemo(
    () => organizations.find((o) => o.id === organizationId) ?? null,
    [organizations, organizationId],
  );

  const displayName =
    [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
    user?.email ||
    'Signed in';

  const canSwitch = organizations.length > 1;

  // Navigating closes the drawer. Keyed on the path rather than wired to each
  // NavLink's onClick, so a link added later cannot forget to do it.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  // Growing past the breakpoint closes it too. Without this, a drawer opened on
  // a phone and then rotated to landscape leaves the scroll lock below in place
  // against a sidebar that is no longer a drawer, and the page cannot scroll at
  // all.
  useEffect(() => {
    const desktop = window.matchMedia(DESKTOP_QUERY);
    const onChange = (event: MediaQueryListEvent) => {
      if (event.matches) setMenuOpen(false);
    };
    desktop.addEventListener('change', onChange);
    return () => desktop.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);

    // The page behind must not scroll under the drawer.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    closeButton.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;

      // Put focus back on the button that opened it, but only if it is still
      // inside the drawer — otherwise closing would yank focus away from
      // wherever the person had since moved it.
      //
      // This belongs here rather than in the close handler because the drawer
      // closes three ways: the close button, Escape, and NAVIGATING. That last
      // one is the reason it matters — the link they clicked is inside a panel
      // that is about to go `inert`, and focus on an inert element is dropped
      // to <body>, leaving a keyboard or screen-reader user at the top of the
      // document with no idea where they are.
      if (drawer.current?.contains(document.activeElement)) {
        // A no-op at desktop width, where the button is display:none — which is
        // what we want when the drawer closed because the window grew.
        menuButton.current?.focus();
      }
    };
  }, [menuOpen]);

  function closeMenu() {
    setMenuOpen(false);
  }

  const sidebar = (
    <SidebarContent
      organization={organization}
      organizationCount={organizations.length}
      canSwitch={canSwitch}
      email={user?.email ?? displayName}
      onSwitchOrganization={() => dispatch(organizationCleared())}
      onSignOut={() => void dispatch(signOut())}
    />
  );

  return (
    <div className="flex min-h-screen flex-col lg:grid lg:grid-cols-[248px_minmax(0,1fr)]">
      {/* Desktop. `display: none` below lg, so it is not in the accessibility
          tree there and the drawer is the only navigation. */}
      <aside className="zs-on-dark hidden bg-ink px-[14px] py-[20px] text-page lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col lg:gap-[24px]">
        {sidebar}
      </aside>

      {/* The bar that replaces it. Sticky rather than fixed, so nothing has to
          reserve space for it. */}
      <header className="zs-on-dark sticky top-0 z-30 flex items-center gap-[12px] bg-ink px-[14px] py-[12px] lg:hidden">
        <button
          ref={menuButton}
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-expanded={menuOpen}
          aria-controls="shell-navigation"
          className="flex shrink-0 items-center justify-center rounded-button border-0 bg-brand-deep p-[10px] text-page transition hover:bg-brand"
        >
          <MenuIcon />
          <span className="zs-sr-only">Open the menu</span>
        </button>

        <Logo width={112} className="shrink-0" />

        <span className="ml-auto min-w-0 truncate text-caption text-sky-pale">
          {organization?.name ?? 'No organization'}
        </span>
      </header>

      {/* The drawer. Always mounted below lg so it can slide both ways, and
          `inert` while closed — which takes it out of the accessibility tree
          and out of the tab order without the CSS having to hide it. */}
      <div
        className={clsx('lg:hidden', !menuOpen && 'pointer-events-none')}
        inert={!menuOpen}
      >
        <div
          onClick={closeMenu}
          aria-hidden="true"
          className={clsx(
            'fixed inset-0 z-40 bg-ink/60 transition-opacity duration-200',
            menuOpen ? 'opacity-100' : 'opacity-0',
          )}
        />
        <div
          ref={drawer}
          id="shell-navigation"
          className={clsx(
            'zs-on-dark fixed inset-y-0 left-0 z-50 flex w-[280px] max-w-[86vw] flex-col gap-[24px]',
            'overflow-y-auto bg-ink px-[14px] py-[20px] text-page transition-transform duration-200',
            menuOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <div className="flex items-center justify-between gap-[8px]">
            <Logo width={128} className="mx-[8px] my-[4px]" />
            <button
              ref={closeButton}
              type="button"
              onClick={closeMenu}
              className="flex shrink-0 items-center justify-center rounded-button border-0 bg-brand-deep p-[10px] text-page transition hover:bg-brand"
            >
              <CloseIcon />
              <span className="zs-sr-only">Close the menu</span>
            </button>
          </div>
          {sidebar}
        </div>
      </div>

      <main className="min-w-0 flex-1 bg-page">
        <Outlet />
      </main>
    </div>
  );
}

/**
 * Everything inside the sidebar, rendered into both the desktop column and the
 * drawer. One definition, so the two can never drift apart — a nav item added
 * to one and not the other is the kind of thing nobody notices until a customer
 * on a phone cannot reach a screen.
 *
 * The logo is NOT here: desktop wants it alone at the top, the drawer wants it
 * beside a close button.
 */
function SidebarContent({
  organization,
  organizationCount,
  canSwitch,
  email,
  onSwitchOrganization,
  onSignOut,
}: {
  organization: { name: string; domain?: string | null } | null;
  organizationCount: number;
  canSwitch: boolean;
  email: string;
  onSwitchOrganization: () => void;
  onSignOut: () => void;
}) {
  return (
    <>
      {/* The organization switcher. Every way back to another organization
          goes through the picker rather than swapping in place: a half-loaded
          screen from the previous tenant is how a mapping gets applied to the
          wrong organization. */}
      <button
        type="button"
        onClick={onSwitchOrganization}
        disabled={!canSwitch}
        className={clsx(
          'flex items-center gap-[10px] rounded-image border-0 bg-brand-deep p-[12px] px-[12px] text-left transition',
          canSwitch ? 'cursor-pointer hover:bg-brand' : 'cursor-default',
        )}
      >
        {organization && <Initials name={organization.name} size={30} />}
        <span className="min-w-0">
          <span className="block truncate text-sub font-semibold text-page">
            {organization?.name ?? 'No organization'}
          </span>
          <span className="block truncate text-micro text-sky-pale">
            {canSwitch
              ? `Switch organization · ${organizationCount}`
              : (organization?.domain ?? 'Your only organization')}
          </span>
        </span>
      </button>

      <nav aria-label="Sections">
        <ul className="m-0 flex list-none flex-col gap-[4px] p-0">
          {NAV.map((item) => (
            <li key={item.to}>
              {item.ready ? (
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    clsx(
                      'block rounded-button px-[12px] py-[10px] text-sub no-underline transition',
                      isActive
                        ? 'bg-brand font-semibold text-page'
                        : 'font-medium text-sky-mist hover:bg-brand-deep',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ) : (
                <span
                  aria-disabled="true"
                  className="flex cursor-default items-center justify-between gap-[8px] rounded-button px-[12px] py-[10px] text-sub font-medium text-sky-pale/60"
                >
                  {item.label}
                  <span className="zs-sr-only"> — not available yet</span>
                  <span
                    aria-hidden="true"
                    className="rounded-chip bg-white/10 px-[8px] py-[2px] text-micro"
                  >
                    soon
                  </span>
                </span>
              )}
            </li>
          ))}
        </ul>
      </nav>

      <div className="mt-auto rounded-image bg-brand-deep p-[14px] text-caption text-sky-mist">
        <p className="zs-eyebrow m-0 mb-[6px] text-micro text-green-soft">Adapter scope</p>
        Mapping, identity and sync live here. Bookings, policies, displays and sensors run in
        ZenCore and ZenEdge.
      </div>

      <div className="flex items-center justify-between gap-[8px] px-[8px] text-caption">
        <span className="min-w-0 truncate text-sky-pale" title={email}>
          {email}
        </span>
        <button
          type="button"
          onClick={onSignOut}
          className="shrink-0 border-0 bg-transparent p-0 text-caption font-semibold text-green-soft"
        >
          Sign out
        </button>
      </div>

      <BuildStamp />
    </>
  );
}

/**
 * Which build is on screen.
 *
 * Small and quiet, because it is for whoever is debugging rather than for the
 * person booking a room — but it is the first question asked whenever the
 * behaviour and the code disagree, and answering it by reading a bundle hash in
 * the network tab is no way to spend an afternoon.
 *
 * The commit is inlined at compile time, so this reports the build actually
 * being executed rather than whatever is currently checked out. The full build
 * timestamp is in the tooltip: the line has to stay short, and the date is
 * rarely the thing being asked about.
 */
function BuildStamp() {
  const version = import.meta.env.VITE_APP_VERSION ?? '0.0.0';
  const sha = import.meta.env.VITE_BUILD_SHA ?? 'unknown';
  const builtAt = import.meta.env.VITE_BUILT_AT;

  return (
    <p
      className="m-0 px-[8px] text-micro text-sky-pale/70"
      title={builtAt ? `Built ${new Date(builtAt).toLocaleString()}` : undefined}
    >
      v{version} · <span className="font-mono">{sha}</span>
    </p>
  );
}

function MenuIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M2 4.5h14M2 9h14M2 13.5h14"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M4 4l10 10M14 4L4 14"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
